/**
 * One guide, open.
 *
 * The page is a reading surface first: a column of prose with a contents rail
 * beside it, and the editing machinery kept to the head so it is never in the
 * way of somebody who came here to find out what to do next.
 *
 * Two states are worth naming out loud rather than hiding:
 *   - a seeded guide the team has since edited says so, because "where did this
 *     text come from" is the first question anybody asks of a procedure; and
 *   - a guide whose master text has moved on says THAT, with both versions one
 *     click apart. Silently keeping the old one is how a handbook drifts.
 */
import { Suspense, lazy, useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Clock3, History, Pencil, RefreshCw, Users, WifiOff } from "lucide-react";
import { LoadingState } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { hostSiteId, userCanPlatform, type StaffUser } from "../../../lib/auth";
import { MediaPicker } from "../../../components/MediaPicker";
import { adoptGuideSource, clearGuideShot, setGuideShot, type KbArticle } from "../../../lib/kb";
import { Btn, Card, EmptyState, StatusPill, fmtDate, useToast } from "../../ui";
import { ErrorBoundary } from "../../shell/ErrorBoundary";
import { KB_CATEGORY_META } from "./meta";
import { ArticleBody } from "./ArticleBody";
import { RevisionsModal } from "./RevisionsModal";
import { useGuide } from "./useGuides";

// Lazy: the flow map brings a canvas editor with it, and the guide's text must
// never wait for that code. The section draws nothing until it knows there is a
// map (or an editor to invite), so no fallback is needed.
const GuideFlowSection = lazy(() => import("./GuideFlowSection").then((m) => ({ default: m.GuideFlowSection })));

export function GuideArticlePage({ user }: { user: StaffUser }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const canEdit = userCanPlatform(user, "manage:crm.kb");
  const siteId = hostSiteId(user);

  const { article, loading, missing, error, setArticle } = useGuide(slug);
  const [picking, setPicking] = useState<string | null>(null);
  const [history, setHistory] = useState(false);
  const [adopting, setAdopting] = useState(false);

  const applyPick = useCallback(
    async (url: string, mediaId: string | undefined) => {
      if (!article || !picking) return;
      const key = picking;
      setPicking(null);
      try {
        // Video by extension: the picker returns whatever the library holds, and
        // an <img> pointing at an mp4 is a broken frame with no error.
        const kind = /\.(mp4|webm|mov)(\?|$)/i.test(url) ? "video" : "image";
        setArticle(await setGuideShot(article.id, key, { url, kind, mediaId }));
      } catch {
        toast(t("kb.shot.failed"));
      }
    },
    [article, picking, setArticle, t, toast],
  );

  const dropShot = useCallback(
    async (key: string) => {
      if (!article) return;
      try {
        setArticle(await clearGuideShot(article.id, key));
      } catch {
        toast(t("kb.shot.failed"));
      }
    },
    [article, setArticle, t, toast],
  );

  async function adopt() {
    if (!article) return;
    setAdopting(true);
    try {
      const next = await adoptGuideSource(article.id);
      setArticle(next);
      toast(t("kb.source.adopted"));
      if (next.slug !== article.slug) navigate(`/guides/${next.slug}`, { replace: true });
    } catch {
      toast(t("kb.source.adoptFailed"));
    } finally {
      setAdopting(false);
    }
  }

  if (loading && !article) return <div className="crm-page"><LoadingState label={t("common.loading")} /></div>;

  if (missing || error || !article) {
    return (
      <div className="crm-page">
        <EmptyState
          icon={error ? WifiOff : BookOpen}
          title={error ? t("kb.errorTitle") : t("kb.missingTitle")}
          body={error ? t("kb.errorBody") : t("kb.missingBody")}
          action={<Btn onClick={() => navigate("/guides")}>{t("kb.backToIndex")}</Btn>}
        />
      </div>
    );
  }

  const meta = KB_CATEGORY_META[article.category as keyof typeof KB_CATEGORY_META];
  const Icon = meta?.icon ?? BookOpen;

  return (
    <div className="crm-page crm-kb-read">
      <div className="crm-kb-crumbs">
        <Link to="/guides">
          <ArrowLeft className="size-4" />
          {t("kb.title")}
        </Link>
        <span>/</span>
        <span>
          <Icon className="size-3.5" />
          {meta ? t(meta.labelKey) : article.category}
        </span>
      </div>

      <header className="crm-kb-head">
        <div>
          <h1>{article.title}</h1>
          <p className="crm-kb-lede">{article.summary}</p>
          <div className="crm-kb-meta">
            {article.audience ? (
              <span>
                <Users className="size-3.5" />
                {article.audience}
              </span>
            ) : null}
            <span>
              <Clock3 className="size-3.5" />
              {t("kb.minutes", { n: article.readMinutes })}
            </span>
            <span>{t("kb.updated", { date: fmtDate(article.updatedAt) })}</span>
            {article.status === "draft" ? <StatusPill tone="wt">{t("kb.draft")}</StatusPill> : null}
            {article.editedAt ? (
              <StatusPill tone="info">
                {t("kb.editedBy", { who: article.editedByName ?? t("kb.someone") })}
              </StatusPill>
            ) : null}
          </div>
        </div>
        {canEdit ? (
          <div className="crm-kb-head-act">
            <Btn small onClick={() => setHistory(true)}>
              <History className="size-4" />
              {t("kb.history")}
            </Btn>
            <Btn tone="dark" small onClick={() => navigate(`/guides/${article.slug}/muuda`)}>
              <Pencil className="size-4" />
              {t("kb.edit")}
            </Btn>
          </div>
        ) : null}
      </header>

      {article.sourceStale ? (
        <Card className="crm-kb-stale">
          <div>
            <strong>{t("kb.source.staleTitle")}</strong>
            <p>{t("kb.source.staleBody")}</p>
          </div>
          {canEdit ? (
            <Btn small onClick={adopt} disabled={adopting}>
              <RefreshCw className="size-4" />
              {t("kb.source.adopt")}
            </Btn>
          ) : null}
        </Card>
      ) : null}

      {/* Asked for only when there is a map to draw, or an editor who may start
          one: a reader of the other guides never pays for the request.

          Its own boundary: a map chunk that fails to load (a tab left open
          across a deploy) costs the guide its map, never its text. */}
      {article.hasFlowMap || canEdit ? (
        <ErrorBoundary resetKey={article.id} fallback={() => null}>
          <Suspense fallback={null}>
            <GuideFlowSection key={article.id} article={article} canEdit={canEdit} />
          </Suspense>
        </ErrorBoundary>
      ) : null}

      <div className="crm-kb-layout">
        <ArticleBody article={article} canEdit={canEdit} onPick={setPicking} onClear={dropShot} />

        {article.headings.length > 1 ? (
          <nav className="crm-kb-toc" aria-label={t("kb.contents")}>
            <span>{t("kb.contents")}</span>
            <ul>
              {article.headings.map((h) => (
                <li key={h.id}>
                  <a href={`#${h.id}`}>{h.text}</a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </div>

      {history ? (
        <RevisionsModal article={article} onClose={() => setHistory(false)} onRestored={setArticle} />
      ) : null}

      {siteId ? (
        <MediaPicker
          open={picking !== null}
          siteId={siteId}
          onClose={() => setPicking(null)}
          onPick={(asset) => void applyPick(asset.url, asset.id)}
        />
      ) : null}
    </div>
  );
}
